import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import CartItem from './components/CartItem';
import PromoCodeSection from './components/PromoCodeSection';
import OrderSummary from './components/OrderSummary';
import EmptyCart from './components/EmptyCart';
import RecommendedProducts from './components/RecommendedProducts';
import SavedForLater from './components/SavedForLater';

const ShoppingCart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load cart data on component mount
  useEffect(() => {
    loadCartData();
  }, []);

  const loadCartData = () => {
    setIsLoading(true);
    
    // Load cart items from localStorage
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      const items = JSON.parse(savedCart).map(item => ({
        ...item,
        stock: item.stock || 50 // Default stock if not provided
      }));
      setCartItems(items);
    }

    // Load saved items from localStorage
    const savedForLater = localStorage.getItem('savedForLater');
    if (savedForLater) {
      setSavedItems(JSON.parse(savedForLater));
    }

    // Load applied promo from localStorage
    const savedPromo = localStorage.getItem('appliedPromo');
    const savedDiscount = localStorage.getItem('discount');
    if (savedPromo && savedDiscount) {
      setAppliedPromo(savedPromo);
      setDiscount(JSON.parse(savedDiscount));
    }

    setIsLoading(false);
  };

  const updateQuantity = (itemId, newQuantity) => {
    const updatedItems = cartItems.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    );
    setCartItems(updatedItems);
    localStorage.setItem('cart', JSON.stringify(updatedItems));
  };

  const removeItem = (itemId) => {
    const updatedItems = cartItems.filter(item => item.id !== itemId);
    setCartItems(updatedItems);
    localStorage.setItem('cart', JSON.stringify(updatedItems));
  };

  const saveForLater = (itemId) => {
    const itemToSave = cartItems.find(item => item.id === itemId);
    if (itemToSave) {
      const updatedSavedItems = [...savedItems, itemToSave];
      setSavedItems(updatedSavedItems);
      localStorage.setItem('savedForLater', JSON.stringify(updatedSavedItems));
      
      removeItem(itemId);
    }
  };

  const moveToCart = (item) => {
    const cartItem = { ...item, quantity: 1 };
    const updatedCartItems = [...cartItems, cartItem];
    setCartItems(updatedCartItems);
    localStorage.setItem('cart', JSON.stringify(updatedCartItems));
    
    removeFromSaved(item.id);
  };

  const removeFromSaved = (itemId) => {
    const updatedSavedItems = savedItems.filter(item => item.id !== itemId);
    setSavedItems(updatedSavedItems);
    localStorage.setItem('savedForLater', JSON.stringify(updatedSavedItems));
  };

  const applyPromoCode = (code, discountInfo) => {
    setAppliedPromo(code);
    setDiscount(discountInfo);
    
    if (code && discountInfo) {
      localStorage.setItem('appliedPromo', code);
      localStorage.setItem('discount', JSON.stringify(discountInfo));
    } else {
      localStorage.removeItem('appliedPromo');
      localStorage.removeItem('discount');
    }
  };

  const addRecommendedToCart = (product) => {
    const existingItemIndex = cartItems.findIndex(item => item.id === product.id);
    
    if (existingItemIndex > -1) {
      updateQuantity(product.id, cartItems[existingItemIndex].quantity + 1);
    } else {
      const updatedItems = [...cartItems, product];
      setCartItems(updatedItems);
      localStorage.setItem('cart', JSON.stringify(updatedItems));
    }
  };

  const handleProceedToCheckout = () => {
    if (cartItems.length === 0) return;
    navigate('/checkout-process');
  };

  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  
  let discountAmount = 0;
  if (discount) {
    discountAmount = discount.type === 'percentage' 
      ? (subtotal * discount.discount) / 100
      : discount.discount;
  }
  
  const deliveryFee = subtotal >= 50 ? 0 : 5.99;
  const tax = (subtotal - discountAmount) * 0.08; // 8% tax
  const total = subtotal - discountAmount + deliveryFee + tax;

  const breadcrumbItems = [
    { label: 'Home', path: '/homepage-dashboard' },
    { label: 'Shopping Cart', path: '/shopping-cart' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading your cart...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb customItems={breadcrumbItems} />
          
          <div className="mb-8">
            <h1 className="font-heading font-heading-bold text-3xl lg:text-4xl text-text-primary mb-2">
              Shopping Cart
            </h1>
            {cartItems.length > 0 && (
              <p className="text-text-secondary">
                {itemCount} {itemCount === 1 ? 'item' : 'items'} in your cart
              </p>
            )}
          </div>

          {cartItems.length === 0 ? (
            <EmptyCart />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items Section */}
              <div className="lg:col-span-2">
                <div className="space-y-4">
                  {cartItems.map((item) => (
                    <CartItem
                      key={item.id}
                      item={item}
                      onUpdateQuantity={updateQuantity}
                      onRemove={removeItem}
                      onSaveForLater={saveForLater}
                    />
                  ))}
                </div>

                {/* Promo Code Section */}
                <PromoCodeSection
                  onApplyPromo={applyPromoCode}
                  appliedPromo={appliedPromo}
                  discount={discount}
                />

                {/* Saved for Later */}
                <SavedForLater
                  savedItems={savedItems}
                  onMoveToCart={moveToCart}
                  onRemoveFromSaved={removeFromSaved}
                />
              </div>

              {/* Order Summary & Recommendations */}
              <div className="space-y-6">
                <OrderSummary
                  subtotal={subtotal}
                  discount={discountAmount}
                  deliveryFee={deliveryFee}
                  tax={tax}
                  total={total}
                  itemCount={itemCount}
                  onProceedToCheckout={handleProceedToCheckout}
                />

                <RecommendedProducts onAddToCart={addRecommendedToCart} />
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ShoppingCart;